import { ReactNode, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DndContext,
  DragEndEvent,
  Modifier,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ScorecardGoal } from 'store/interfaces';
import { usePagePermissions, useScorecardStates } from 'hooks';
import { SCORECARD_GOAL_TYPE_METRIC } from 'constants/scorecards';
import GoalTypeIcon from '../goalTypeIcons';
import {
  TabButton,
  TabLabel,
  TabStripActions,
  TabStripRoot,
  TabsScrollFade,
  TabsScroller,
  TabsScrollerWrapper,
} from './TabStrip.styled';

export const ALL_GOALS = 'all' as const;
export type ActiveTab = number | typeof ALL_GOALS;

const restrictToHorizontalAxis: Modifier = ({ transform }) => ({
  ...transform,
  y: 0,
});

interface TabStripProps {
  goals: ScorecardGoal[];
  active: ActiveTab;
  onChange: (next: ActiveTab) => void;
  onReorder?: (fromIdx: number, toIdx: number) => void;
  actions?: ReactNode;
  showWeight?: boolean;
}

interface GoalTabProps {
  goal: ScorecardGoal;
  isActive: boolean;
  onSelect: () => void;
  sortable: boolean;
  showWeight: boolean;
  showScore: boolean;
}

// Mirrors `OLD_SCORECARD_DATA/.../ScorecardDetails.component.tsx:79` — a metric goal always shows
// its score, every other type only once the viewer is allowed to see computed details.
const getScoreDisplay = (goal: ScorecardGoal, showScore: boolean): string => {
  const score = goal.totalScoreFinal;
  if (
    goal.type === SCORECARD_GOAL_TYPE_METRIC &&
    score !== null &&
    score !== undefined
  ) {
    return ` | ${score}`;
  }
  if (showScore) {
    return ` | ${score || 0}`;
  }
  return '';
};

function GoalTab({
  goal,
  isActive,
  onSelect,
  sortable,
  showWeight,
  showScore,
}: GoalTabProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: goal.id, disabled: !sortable });

  const style = sortable
    ? {
        transform: CSS.Translate.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
      }
    : undefined;

  const sortableAttrs = sortable
    ? (Object.fromEntries(
        Object.entries(attributes).filter(([key]) => key !== 'role'),
      ) as typeof attributes)
    : ({} as typeof attributes);

  return (
    <TabButton
      ref={sortable ? setNodeRef : undefined}
      type="button"
      isActive={isActive}
      onClick={onSelect}
      style={style}
      {...sortableAttrs}
      {...(sortable ? listeners : {})}
      role="tab"
    >
      <TabLabel>
        <GoalTypeIcon type={goal.type} sx={{ fontSize: 16 }} />
        <span>
          {showWeight
            ? `${goal.name} (${goal.weight ?? 0}%${getScoreDisplay(
                goal,
                showScore,
              )})`
            : goal.name}
        </span>
      </TabLabel>
    </TabButton>
  );
}

export default function TabStrip({
  goals,
  active,
  onChange,
  onReorder,
  actions,
  showWeight = true,
}: TabStripProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const { hasFullAccess } = usePagePermissions();
  const { isFeedbackRequested, isFeedbackCollected, isCompleted } =
    useScorecardStates();
  const showScore =
    (hasFullAccess && (isFeedbackRequested || isFeedbackCollected)) ||
    isCompleted;

  const scrollerRef = useRef<HTMLDivElement>(null);
  const prevGoalCount = useRef(goals.length);
  const [hasMoreTabs, setHasMoreTabs] = useState(false);

  useEffect(() => {
    if (goals.length > prevGoalCount.current && scrollerRef.current) {
      const scroller = scrollerRef.current;
      scroller.scrollLeft = scroller.scrollWidth;
    }
    prevGoalCount.current = goals.length;
  }, [goals.length]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return undefined;

    // 1px of slack: fractional layout widths can leave scrollLeft a hair short
    // of the true end, which would keep the fade up on a fully scrolled strip.
    const sync = () =>
      setHasMoreTabs(
        scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft > 1,
      );

    sync();
    scroller.addEventListener('scroll', sync);
    const observer = new ResizeObserver(sync);
    observer.observe(scroller);

    return () => {
      scroller.removeEventListener('scroll', sync);
      observer.disconnect();
    };
  }, [goals.length]);

  const handleDragEnd = (event: DragEndEvent) => {
    if (!onReorder) return;
    const { active: dragged, over } = event;
    if (!over || dragged.id === over.id) return;
    const fromIdx = goals.findIndex((g) => g.id === dragged.id);
    const toIdx = goals.findIndex((g) => g.id === over.id);
    if (fromIdx === -1 || toIdx === -1) return;
    onReorder(fromIdx, toIdx);
  };

  const sortable = Boolean(onReorder);

  const goalTabs = goals.map((goal) => (
    <GoalTab
      key={goal.id}
      goal={goal}
      isActive={active === goal.id}
      onSelect={() => onChange(goal.id)}
      sortable={sortable}
      showWeight={showWeight}
      showScore={showScore}
    />
  ));

  return (
    <TabStripRoot>
      <TabsScrollerWrapper>
        <TabsScroller role="tablist" ref={scrollerRef}>
          <TabButton
            role="tab"
            type="button"
            isActive={active === ALL_GOALS}
            onClick={() => onChange(ALL_GOALS)}
          >
            <FormattedMessage
              id="pages.scorecards.view.allGoals"
              defaultMessage="All goals ({count})"
              values={{ count: goals.length }}
            />
          </TabButton>
          {sortable ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToHorizontalAxis]}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={goals.map((g) => g.id)}
                strategy={horizontalListSortingStrategy}
              >
                {goalTabs}
              </SortableContext>
            </DndContext>
          ) : (
            goalTabs
          )}
        </TabsScroller>
        {hasMoreTabs && <TabsScrollFade />}
      </TabsScrollerWrapper>
      {actions && <TabStripActions>{actions}</TabStripActions>}
    </TabStripRoot>
  );
}
